import { useState } from 'react'
import { ChevronLeft, ChevronRight, ExternalLink, Sparkles, Star } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const OPINIONS_URL = 'https://www.ofertator.pl/#opinie'
const TESTIMONIAL_VIDEO_ID = 'LKFKJ3nrpEc'
const TESTIMONIAL_VIDEO_EMBED_URL = `https://www.youtube.com/embed/${TESTIMONIAL_VIDEO_ID}?autoplay=1&mute=1&loop=1&playlist=${TESTIMONIAL_VIDEO_ID}&playsinline=1`

const CASE_STUDIES = [
  {
    imageUrl:
      'https://framerusercontent.com/images/U9yub9SAJoeUh0H8qUkqYmbDAU.png?scale-down-to=512&width=1500&height=1200',
    altKey: 'kitchenScale',
  },
  {
    imageUrl:
      'https://framerusercontent.com/images/dmpxDXiBF8NIK5z7O7NAn2Slw.png?scale-down-to=512&width=1500&height=1200',
    altKey: 'hairStraightener',
  },
] as const

const FEATURED_KEYS = ['szymon', 'mateusz'] as const

function FeaturedStoryStars() {
  return (
    <div className="mt-3 flex gap-0.5" aria-hidden>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} className="h-3.5 w-3.5 fill-white text-white" />
      ))}
    </div>
  )
}

export function UpgradePlanSocialProof() {
  const { t } = useTranslation('credits')
  const [storyIndex, setStoryIndex] = useState(0)

  const activeKey = FEATURED_KEYS[storyIndex]
  const totalStories = FEATURED_KEYS.length

  const goPrev = () => setStoryIndex((i) => (i - 1 + totalStories) % totalStories)
  const goNext = () => setStoryIndex((i) => (i + 1) % totalStories)

  return (
    <section className="mt-4 rounded-xl border border-gray-200 bg-gray-50/80 overflow-hidden">
      <div className="flex flex-col gap-2 border-b border-gray-200 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-bold text-gray-900">{t('upgradeTopBar.socialProof.title')}</p>
          <p className="text-xs text-gray-500">{t('upgradeTopBar.socialProof.subtitle')}</p>
        </div>
        <a
          href={OPINIONS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs font-semibold text-[#FF5A00] hover:underline shrink-0"
        >
          {t('upgradeTopBar.socialProof.viewAll')}
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>

      <div className="grid grid-cols-3 divide-x divide-gray-200 border-b border-gray-200 bg-white">
        {(['rating', 'faster', 'quality'] as const).map((key) => (
          <div key={key} className="px-3 py-2.5 text-center">
            <p className="text-sm font-bold tabular-nums text-gray-900 flex items-center justify-center gap-0.5">
              {key === 'rating' && <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />}
              {t(`upgradeTopBar.socialProof.stats.${key}`)}
            </p>
            <p className="mt-0.5 text-[10px] leading-snug text-gray-500">
              {t(`upgradeTopBar.socialProof.stats.${key}Label`)}
            </p>
          </div>
        ))}
      </div>

      <div className="p-4 space-y-4">
        <div className="overflow-hidden rounded-xl bg-[#212121]">
          <div className="grid grid-cols-1 sm:grid-cols-[minmax(140px,200px)_1fr]">
            <div className="relative flex items-center justify-center bg-black p-3 sm:p-4">
              <div className="relative w-full max-w-[180px] sm:max-w-none">
                <iframe
                  src={TESTIMONIAL_VIDEO_EMBED_URL}
                  className="block w-full aspect-[9/16] rounded-lg border-0"
                  allow="autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                  loading="lazy"
                  title={t('upgradeTopBar.socialProof.videoAria')}
                />
              </div>
            </div>

            <div className="flex min-h-[240px] flex-col justify-between p-4 sm:p-5 text-white">
              <div>
                <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-gray-400">
                  <Sparkles className="h-3 w-3" />
                  {t('upgradeTopBar.socialProof.storiesLabel')}
                </p>
                <blockquote className="mt-3 text-sm sm:text-[15px] leading-relaxed text-gray-100">
                  „{t(`upgradeTopBar.socialProof.featured.${activeKey}.quote`)}”
                </blockquote>
                <FeaturedStoryStars />
                <p className="mt-3 text-sm font-bold text-white">
                  {t(`upgradeTopBar.socialProof.featured.${activeKey}.author`)}
                </p>
                <p className="text-xs text-gray-400">
                  {t(`upgradeTopBar.socialProof.featured.${activeKey}.role`)}
                </p>
              </div>

              <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-3">
                <span className="text-xs tabular-nums text-gray-400">
                  {t('upgradeTopBar.socialProof.storyCounter', {
                    current: String(storyIndex + 1).padStart(2, '0'),
                    total: String(totalStories).padStart(2, '0'),
                  })}
                </span>
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    onClick={goPrev}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/20 text-white hover:bg-white/10 transition-colors"
                    aria-label={t('upgradeTopBar.socialProof.prevStory')}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={goNext}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-[#212121] hover:bg-gray-100 transition-colors"
                    aria-label={t('upgradeTopBar.socialProof.nextStory')}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div>
          <p className="text-[10px] font-bold uppercase tracking-wide text-gray-500 mb-2">
            {t('upgradeTopBar.socialProof.caseStudiesTitle')}
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            {CASE_STUDIES.map(({ imageUrl, altKey }) => (
              <a
                key={altKey}
                href={OPINIONS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="block overflow-hidden rounded-lg border border-gray-200 bg-white hover:border-orange-200 transition-colors"
              >
                <img
                  src={imageUrl}
                  alt={t(`upgradeTopBar.socialProof.caseStudies.${altKey}`)}
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
              </a>
            ))}
          </div>
        </div>

        <a
          href={OPINIONS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:border-[#FF5A00] hover:text-[#FF5A00] transition-colors"
        >
          {t('upgradeTopBar.socialProof.moreReviews')}
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
    </section>
  )
}
